import { Box, Card, CardContent, Chip, Typography } from "@mui/joy";
import { Target, CheckCircle2, Hammer, Pause, CalendarDays, TrendingUp, TrendingDown } from "lucide-react";
import { useProject } from "../../../../hooks/query";
import { useEffect } from "react";

type DashboardCardProps = {
    title: string;
    icon: React.ReactNode;
    value: number | string;
    change?: string;
    timeRange?: "All time" | "Last Month" | "Current Month";
    customColorScheme?: any;
};

function getMonthYear(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${date.getMonth() + 1}`;
}

function calculateChange(current: number, previous: number): string {
    if (previous === 0 && current === 0) return "0%";
    if (previous === 0) return "+100%";
    const diff = ((current - previous) / previous) * 100;
    return `${diff > 0 ? "+" : ""}${diff.toFixed(1)}%`;
}

export default function ProjectStats() {
    const { useList } = useProject();
    const listProjectSvc = useList();

    useEffect(() => {
        listProjectSvc.mutateAsync().catch(console.error);
    }, []);

    const projects = listProjectSvc.data || [];

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const currentKey = `${currentYear}-${currentMonth}`;
    const lastMonthKey = `${currentMonth === 1 ? currentYear - 1 : currentYear}-${
        currentMonth === 1 ? 12 : currentMonth - 1
    }`;

    const currentMonthProjects = projects.filter((p: any) => getMonthYear(p.CreatedAt) === currentKey);
    const lastMonthProjects = projects.filter((p: any) => getMonthYear(p.CreatedAt) === lastMonthKey);

    const totalCurrent = currentMonthProjects.length;
    const totalLast = lastMonthProjects.length;

    const inProgressCurrent = currentMonthProjects.filter((p: any) => p.Status === "IN-PROGRESS").length;
    const inProgressLast = lastMonthProjects.filter((p: any) => p.Status === "IN-PROGRESS").length;

    const completedCurrent = currentMonthProjects.filter((p: any) => p.Status === "COMPLETED").length;
    const completedLast = lastMonthProjects.filter((p: any) => p.Status === "COMPLETED").length;

    const notStartedCurrent = currentMonthProjects.filter((p: any) => p.Status === "NOT-STARTED").length;
    return (
        <Box display={"flex"} flexWrap="wrap" gap={4} mt={0}>
            <DashboardCard
                title="Total Projects"
                value={projects.length}
                icon={<CalendarDays size={20} />}
                colorTheme="primary"
            />
            <DashboardCard
                title="In Progress"
                value={inProgressCurrent}
                icon={<Hammer size={20} />}
                colorTheme="warning"
            />
            <DashboardCard
                title="Not Started"
                value={notStartedCurrent}
                icon={<Pause size={20} />}
                colorTheme="success"
            />
        </Box>
    );
}

function DashboardCard({ title, value, icon, colorTheme }: any) {
    return (
        <Card 
            variant="plain" 
            sx={{ 
                minWidth: 200, 
                flex: 1, 
                bgcolor: "background.surface", 
                boxShadow: "sm", 
                borderRadius: "xl",
                p: 3,
                border: "none",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between"
            }}
        >
            <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography level="body-sm" sx={{ fontWeight: 600, color: "text.tertiary" }}>
                    {title}
                </Typography>
                <Typography level="h1" sx={{ fontWeight: 800, fontSize: "2.4rem", color: "text.primary" }}>
                    {value}
                </Typography>
            </Box>
            <Box 
                sx={{ 
                    width: 50, 
                    height: 50, 
                    borderRadius: "50%", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    bgcolor: `${colorTheme}.softBg`,
                    color: `${colorTheme}.500`
                }}
            >
                {icon}
            </Box>
        </Card>
    );
}
